import React, { useEffect } from 'react'
import { toast } from 'react-toastify'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Grid, TextField, Tooltip } from '@mui/material'
import { IconButton, InputAdornment } from '@mui/material'
import Button from '@mui/material/Button'
import { styled } from '@mui/material/styles'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { useMutation } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import RewardsAPI from 'api/faber/RewardsAPI'
import NormalText from 'common/Typography/NormalText'
import { Form, Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

const CustomDatePicker = (props: DatePickerProps) => {
  const { label, disabled, date, setDate } = props

  const handleStartDate = (newValue: Date | null) => {
    setDate(newValue)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        inputFormat='dd-MM-yyyy HH:00'
        views={['year', 'month', 'day', 'hours']}
        value={date}
        onChange={handleStartDate}
        renderInput={(params) => (
          <TextField placeholder='Date' variant='standard' {...params} />
        )}
        disabled={disabled}
      />
    </LocalizationProvider>
  )
}

export default CustomDatePicker

interface DatePickerProps {
  label: string
  disabled: boolean
  date: Date | null
  setDate: (value: Date | null) => void
}

const formikInitialValues = {
  campaignRewardName: '',
  campaignSummary: '',
  campaignCode: '',
  campaignCodePrefix: '',
  campaignType: '',
  link: '',
  rewardProductId: '',
  maxClaims: 0,
  maxConsumption: 0,
  validityStartAt: 0,
  validityEndAt: 0,
  promoRule: []
}

const NewCampaignPromoSchema = Yup.object().shape({
  campaignRewardName: Yup.string().required('Campaign promo name is required'),
  campaignSummary: Yup.string().required('Campaign promo summary is required'),
  campaignCode: Yup.string().required('Campaign promo code is required'),
  campaignType: Yup.string(),
  link: Yup.string().url('Must be a valid URL').optional(),
  maxClaims: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Max claims amount is required'),
  maxConsumption: Yup.number()
    .min(0, 'Input cannot be negative')
    .required('Max consumption amount is required'),
  validityEndAt: Yup.number()
    .min(Yup.ref('validityStartAt'), 'End time cannot be before the start time')
    .required('End time is required')
})

const CenteredBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '80%'
})
const CenteredGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center'
})

const StyledTextField = styled(TextField)({
  '& input': {
    padding: '6px'
  },
  width: '100%'
})

const startDate = new Date()
startDate.setMinutes(0)

const endDate = new Date()
endDate.setMinutes(0)

export const NewCampaignPromoForm = ({
  handleClose
}: {
  handleClose: () => void
}) => {
  const [validityStartAt, setValidityStart] = React.useState<Date | null>(
    startDate
  )
  const [validityEndAt, setValidityEnd] = React.useState<Date | null>(endDate)

  interface RewardRow {
    id: string
    reward_name: string
    reward_description: string
    reward_description_internal: string
  }
  interface RuleRow {
    id: string
    ruleName: string
    ruleDescription: string
    ruleType: string
  }
  const [ruleRows, setRuleRows] = React.useState<RuleRow[]>([])
  const [rewardRows, setRewardRows] = React.useState<RewardRow[]>([])
  const [ruleSelectionModel, setRuleSelectionModel] = React.useState<string[]>(
    []
  )
  const [rewardSelectionModel, setRewardSelectionModel] =
    React.useState<string>('')

  const faberToken = OrbitSession.get('faberToken')
  const { data: rewardProducts } = useQuery(['getRewardProducts'], async () =>
    RewardsAPI.getRewardProducts(faberToken)
  )

  const { data: rewardRules } = useQuery(['getRewardRules'], async () =>
    RewardsAPI.getRewardRules(faberToken)
  )

  const rewardColumns: GridColDef[] = [
    {
      field: 'reward_name',
      headerName: 'Name',
      width: 350
    },
    {
      field: 'reward_description',
      headerName: 'Description',
      width: 350
    },
    {
      field: 'reward_description_internal',
      headerName: 'Internal description',
      width: 350
    }
  ]

  const ruleColumns: GridColDef[] = [
    {
      field: 'ruleName',
      headerName: 'Rule name',
      width: 350
    },
    {
      field: 'ruleDescription',
      headerName: 'Rule description',
      width: 350
    },
    {
      field: 'ruleType',
      headerName: 'Rule type',
      width: 200
    },
    {
      field: 'ruleValue',
      headerName: 'Rule value',
      width: 200
    }
  ]

  useEffect(() => {
    if (rewardRules) {
      setRuleRows(rewardRules)
    }
  }, [rewardRules])

  useEffect(() => {
    if (rewardProducts) {
      setRewardRows(rewardProducts)
    }
  }, [rewardProducts])

  const { mutate: createPromoCode } = useMutation(RewardsAPI.createPromoCode, {
    onSuccess: () => {
      toast.success('Promo code created successfully')
      handleClose()
    },
    onError: () => {
      toast.error('Error creating promo code')
    }
  })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFormSubmit = (values: NewCampaignPromoFormikProps) => {
    const allValues = {
      campaignRewardName: values.campaignRewardName,
      campaignSummary: values.campaignSummary,
      campaignCode: values.campaignCode,
      campaignCodePrefix: values.campaignCodePrefix,
      campaignType: values.campaignType,
      link: values.link,
      rewardProductId: rewardSelectionModel,
      maxClaims: values.maxClaims,
      maxConsumption: values.maxConsumption,
      validityStartAt: values.validityStartAt,
      validityEndAt: values.validityEndAt,
      promoRule: ruleSelectionModel,
      faberToken: faberToken
    }
    createPromoCode(allValues)
  }

  const rowIdToRuleId = ruleRows.reduce((acc, row) => {
    acc[row.id] = row.id
    return acc
  }, {})

  /**
   * Event handlers
   */
  return (
    <Formik
      initialValues={formikInitialValues}
      onSubmit={handleFormSubmit}
      validationSchema={NewCampaignPromoSchema}
    >
      {({
        submitForm,
        handleChange,
        errors,
        touched,
        handleBlur,
        values,
        setFieldValue
      }) => (
        <Form>
          <Grid container rowSpacing={2}>
            {/* CAMPAIGN PROMO NAME */}
            <Grid item xs={3}>
              <NormalText isBold>Name</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='campaignRewardName'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'campaign-name'
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.campaignRewardName && touched.campaignRewardName)
                  }
                  helperText={
                    errors.campaignRewardName && touched.campaignRewardName
                      ? errors.campaignRewardName
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Summary</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-summary'
                  }}
                  name='campaignSummary'
                  placeholder='This will be visible on website, write carefully'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.campaignSummary && touched.campaignSummary)}
                  helperText={
                    errors.campaignSummary && touched.campaignSummary
                      ? errors.campaignSummary
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Promo Code</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-code'
                  }}
                  name='campaignCode'
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase()
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.campaignCode && touched.campaignCode)}
                  helperText={
                    errors.campaignCode && touched.campaignCode
                      ? errors.campaignCode
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NormalText isBold>Prefix</NormalText>
                <Tooltip title='Can be left empty'>
                  <InfoIcon style={{ marginLeft: '5px' }} />
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  inputProps={{
                    'data-testid': 'campaign-code-prefix'
                  }}
                  name='campaignCodePrefix'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  fullWidth
                  variant='outlined'
                  error={
                    !!(errors.campaignCodePrefix && touched.campaignCodePrefix)
                  }
                  helperText={
                    errors.campaignCodePrefix && touched.campaignCodePrefix
                      ? errors.campaignCodePrefix
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <NormalText isBold>Campaign Type</NormalText>
                <Tooltip
                  title={
                    <React.Fragment>
                      <div>WEB: visible in the voucher section always</div>
                      <br></br>
                      <div>
                        PAID/AFFILIATE: Wont be visible in voucher section until
                        user link has Promo
                      </div>
                      <br></br>
                      <div>Keep it blank if not sure</div>
                    </React.Fragment>
                  }
                >
                  <InfoIcon style={{ marginLeft: '5px' }} />
                </Tooltip>
              </div>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='campaignType'
                  onChange={(e) => {
                    e.target.value = e.target.value.toUpperCase()
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'campaign-type'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.campaignType && touched.campaignType)}
                  helperText={
                    errors.campaignType && touched.campaignType
                      ? errors.campaignType
                      : null
                  }
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Link</NormalText>
            </Grid>
            <Grid item xs={9}>
              <Box sx={{ width: '80%' }}>
                <StyledTextField
                  name='link'
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  inputProps={{
                    'data-testid': 'campaign-link'
                  }}
                  fullWidth
                  variant='outlined'
                  error={!!(errors.link && touched.link)}
                  helperText={errors.link && touched.link ? errors.link : null}
                />
              </Box>
            </Grid>
            {values.link && !errors.link && values.campaignCode && (
              <>
                <Grid item xs={3}>
                  <NormalText isBold>Copiable Link</NormalText>
                </Grid>
                <Grid item xs={9}>
                  <Box sx={{ width: '80%' }}>
                    <StyledTextField
                      name='copiableLink'
                      value={`${values.link}/?promoRef=${values.campaignCode}`}
                      fullWidth
                      variant='outlined'
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='copy link'
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  `${values.link}/?promoRef=${values.campaignCode}`
                                )
                                toast.success('Link copied to clipboard!')
                              }}
                              edge='end'
                            >
                              <ContentCopyIcon />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      inputProps={{
                        'data-testid': 'copiable-link'
                      }}
                    />
                  </Box>
                </Grid>
              </>
            )}
            <Grid item xs={3}>
              <NormalText isBold>Reward Product</NormalText>
            </Grid>
            <Grid item xs={12} height={640}>
              <DataGrid
                rows={rewardRows}
                columns={rewardColumns}
                rowsPerPageOptions={[10]}
                pageSize={10}
                selectionModel={rewardSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  setRewardSelectionModel(newSelection[0] as string)
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Max Claims</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxClaims'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxClaims && touched.maxClaims)}
                  helperText={
                    errors.maxClaims && touched.maxClaims
                      ? errors.maxClaims
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Max Consumption</NormalText>
            </Grid>
            <CenteredGrid item xs={9}>
              <CenteredBox>
                <StyledTextField
                  name='maxConsumption'
                  type='number'
                  inputProps={{
                    min: 0
                  }}
                  onChange={(e) => {
                    handleBlur(e)
                    handleChange(e)
                  }}
                  error={!!(errors.maxConsumption && touched.maxConsumption)}
                  helperText={
                    errors.maxConsumption && touched.maxConsumption
                      ? errors.maxConsumption
                      : null
                  }
                />
              </CenteredBox>
            </CenteredGrid>
            <Grid item xs={3}>
              <NormalText isBold>Validity Start Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity Start Date'
                date={validityStartAt}
                setDate={(value: Date | null) => {
                  setValidityStart(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityStartAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Validity End Date</NormalText>
            </Grid>
            <Grid item xs={9}>
              <CustomDatePicker
                label='Validity End Date'
                date={validityEndAt}
                setDate={(value: Date | null) => {
                  setValidityEnd(value)
                  // Convert the Date object to Unix time
                  const unixTime = value ? Math.floor(value.getTime()) : null
                  setFieldValue('validityEndAt', unixTime)
                }}
                disabled={false}
              />
            </Grid>
            <Grid item xs={3}>
              <NormalText isBold>Reward Rules</NormalText>
            </Grid>
            <Grid item xs={12} height={400}>
              <DataGrid
                rows={ruleRows}
                columns={ruleColumns}
                checkboxSelection
                rowsPerPageOptions={[5]}
                pageSize={5}
                selectionModel={ruleSelectionModel}
                onSelectionModelChange={(newSelection) => {
                  const selectedRuleIds = newSelection.map(
                    (rowId) => rowIdToRuleId[rowId]
                  )
                  setRuleSelectionModel(selectedRuleIds)
                }}
              />
            </Grid>
          </Grid>
          <Grid>
            <Box mt={3}>
              {' '}
              {/* Adjust the number to increase or decrease the space */}
              <Button onClick={submitForm}>Save changes</Button>
            </Box>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}
interface NewCampaignPromoFormikProps {
  campaignRewardName: string
  campaignSummary: string
  campaignCode: string
  campaignCodePrefix?: string
  link?: string
  campaignType: string
  rewardProductId: string
  maxClaims: number
  maxConsumption: number
  validityStartAt: number
  validityEndAt: number
  promoRule: string[]
}
